import React, { useState, useEffect, useMemo } from'react';
import { motion } from'framer-motion';
import { Trophy, Music, ExternalLink, Loader2, RefreshCw, Vote, Filter } from'lucide-react';
import { Badge } from'../components/ui/badge';
import { Button } from'../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from'../components/ui/tabs';

import { API_URL } from'../utils/api';

// Level filter options
const LEVEL_FILTERS = [
 { value:'all', label:'All Levels', color:'bg-slate-600' },
 { value:'beginner', label:'Beginner', color:'bg-green-500' },
 { value:'improver', label:'Improver', color:'bg-blue-500' },
 { value:'intermediate', label:'Intermediate', color:'bg-orange-500' },
 { value:'advanced', label:'Advanced', color:'bg-red-500' },
];

export default function PopularDancesPage() {
 const [copperknobDances, setCopperknobDances] = useState([]);
 const [linedancerDances, setLinedancerDances] = useState([]);
 const [eldDances, setEldDances] = useState([]);
 const [loading, setLoading] = useState(true);
 const [lastUpdatedCK, setLastUpdatedCK] = useState(null);
 const [lastUpdatedLD, setLastUpdatedLD] = useState(null);
 const [lastUpdatedELD, setLastUpdatedELD] = useState(null);
 const [levelFilter, setLevelFilter] = useState('all');

 useEffect(() => {
 fetchAllDances();
 }, []);

 const fetchAllDances = async () => {
 setLoading(true);
 try {
 const [ckResponse, ldResponse, eldResponse] = await Promise.all([
 fetch(`${API_URL}/api/top-dances?limit=20`),
 fetch(`${API_URL}/api/top-dances/linedancer?limit=20`),
 fetch(`${API_URL}/api/top-dances/eld?limit=20`)
 ]);
 
 if (ckResponse.ok) {
 const ckData = await ckResponse.json();
 setCopperknobDances(ckData.dances || []);
 setLastUpdatedCK(ckData.last_updated);
 }
 
 if (ldResponse.ok) {
 const ldData = await ldResponse.json();
 setLinedancerDances(ldData.dances || []);
 setLastUpdatedLD(ldData.last_updated);
 }

 if (eldResponse.ok) {
 const eldData = await eldResponse.json();
 setEldDances(eldData.dances || []);
 setLastUpdatedELD(eldData.last_updated);
 }
 } catch (error) {
 console.error('Error fetching dances:', error);
 } finally {
 setLoading(false);
 }
 };

 // Filter dances by level
 const filterByLevel = (dances) => {
 if (levelFilter ==='all') return dances;
 
 return dances.filter(dance => {
 const level = dance.level.toLowerCase();
 switch (levelFilter) {
 case'beginner':
 return level.includes('beginner');
 case'improver':
 return level.includes('improver');
 case'intermediate':
 return level.includes('intermediate');
 case'advanced':
 return level.includes('advanced');
 default:
 return true;
 }
 });
 };

 // Memoized filtered lists
 const filteredCopperknob = useMemo(() => filterByLevel(copperknobDances), [copperknobDances, levelFilter]);
 const filteredLinedancer = useMemo(() => filterByLevel(linedancerDances), [linedancerDances, levelFilter]);
 const filteredEld = useMemo(() => filterByLevel(eldDances), [eldDances, levelFilter]);

 const getLevelColor = (level) => {
 const levelLower = level.toLowerCase();
 if (levelLower.includes('absolute') || levelLower.includes('beginner')) {
 return'bg-green-100 text-green-700';
 } else if (levelLower.includes('improver')) {
 return'bg-blue-100 text-blue-700';
 } else if (levelLower.includes('intermediate')) {
 return'bg-orange-100 text-orange-700';
 } else if (levelLower.includes('advanced')) {
 return'bg-red-100 text-red-700';
 }
 return'bg-slate-100 text-slate-700';
 };

 const getRankBadge = (rank) => {
 if (rank === 1) return'bg-yellow-400 text-yellow-900';
 if (rank === 2) return'bg-slate-300 text-slate-800';
 if (rank === 3) return'bg-amber-600 text-amber-100';
 return'bg-slate-700 text-white';
 };

 const DanceCard = ({ dance, source, index }) => {
 const hasUrl = dance.url && dance.url.startsWith('http');
 
 const CardContent = () => (
 <div className="flex flex-col md:flex-row md:items-center gap-4">
 {/* Rank Badge */}
 <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-lg shrink-0 ${getRankBadge(dance.rank)}`}>
 #{dance.rank}
 </div>
 
 {/* Dance Info */}
 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-2 mb-1">
 <h3 className="text-lg font-bold text-white group-hover:text-violet-400 transition-colors truncate">
 {dance.name}
 </h3>
 {hasUrl && <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-violet-400 shrink-0 mt-1" />}
 </div>
 
 <p className="text-slate-300 text-sm mb-2 truncate">
 {dance.choreographer ||'Unknown Choreographer'}
 </p>
 
 <div className="flex flex-wrap items-center gap-2">
 <Badge className={getLevelColor(dance.level)}>
 {dance.level}
 </Badge>
 <Badge variant="outline" className="border-slate-700 text-slate-300">
 {dance.count} Count
 </Badge>
 <Badge variant="outline" className="border-slate-700 text-slate-300">
 {dance.wall} Wall
 </Badge>
 {source ==='ld' && dance.votes && (
 <span className="text-xs text-slate-300 ml-auto">
 {dance.votes?.toLocaleString()} votes
 </span>
 )}
 {source ==='eld' && dance.views && (
 <span className="text-xs text-slate-300 ml-auto">
 {dance.views?.toLocaleString()} views
 </span>
 )}
 </div>
 </div>
 
 {/* Music */}
 {dance.music && (
 <div className="md:w-48 shrink-0">
 <div className="flex items-center gap-2 text-slate-300 text-sm bg-slate-800/50 rounded-lg px-3 py-2">
 <Music className="w-4 h-4 text-violet-400 shrink-0" />
 <span className="truncate">{dance.music}</span>
 </div>
 </div>
 )}
 </div>
 );
 
 if (hasUrl) {
 return (
 <motion.a
 key={`${source}-${dance.rank}`}
 href={dance.url}
 target="_blank"
 rel="noopener noreferrer"
 onClick={(e) => {
 // Ensure the link opens in new tab
 e.stopPropagation();
 window.open(dance.url,'_blank','noopener,noreferrer');
 e.preventDefault();
 }}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: index * 0.03 }}
 className="block bg-slate-800/80 hover:bg-slate-800/70 border border-slate-700 rounded-xl p-4 md:p-5 transition-all group cursor-pointer"
 data-testid={`dance-card-${source}-${dance.rank}`}
 >
 <CardContent />
 </motion.a>
 );
 }
 
 return (
 <motion.div
 key={`${source}-${dance.rank}`}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: index * 0.03 }}
 className="block bg-slate-800/80 border border-slate-700 rounded-xl p-4 md:p-5"
 data-testid={`dance-card-${source}-${dance.rank}`}
 >
 <CardContent />
 </motion.div>
 );
 };

 return (
 <div className="min-h-screen bg-slate-900">
 {/* Hero Section */}
 <div className="relative bg-gradient-to-b from-violet-900/50 to-slate-950 py-16">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="text-center"
 >
 <div className="flex items-center justify-center gap-3 mb-4">
 <Trophy className="w-10 h-10 text-yellow-400" />
 <h1 className="text-3xl lg:text-4xl font-extrabold text-white" style={{ fontFamily:'Syne, sans-serif' }}>
 Top Line Dance Charts
 </h1>
 </div>
 <p className="text-slate-300 max-w-lg mx-auto mb-8">
 The most popular line dances from two leading sources
 </p>
 
 {/* Level Filter */}
 <div className="flex flex-wrap items-center justify-center gap-2">
 <Filter className="w-4 h-4 text-slate-300 mr-1" />
 {LEVEL_FILTERS.map((filter) => (
 <button
 key={filter.value}
 onClick={() => setLevelFilter(filter.value)}
 className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
 levelFilter === filter.value
 ? `${filter.color} text-white shadow-lg`
 :'bg-slate-800 text-slate-300 hover:bg-slate-700'
 }`}
 data-testid={`filter-${filter.value}`}
 >
 {filter.label}
 </button>
 ))}
 </div>
 </motion.div>
 </div>
 </div>

 {/* Content */}
 <div className="container mx-auto px-6 lg:px-12 py-8">
 {loading ? (
 <div className="flex items-center justify-center py-20">
 <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
 <span className="ml-3 text-slate-300">Loading dance charts...</span>
 </div>
 ) : (
 <Tabs defaultValue="copperknob" className="w-full">
 <TabsList className="grid w-full grid-cols-3 mb-6 bg-slate-800/80">
 <TabsTrigger 
 value="copperknob" 
 className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400"
 data-testid="tab-copperknob"
 >
 <Trophy className="w-4 h-4 mr-2" />
 CopperKnob
 {levelFilter !=='all' && (
 <span className="ml-2 text-xs text-slate-300">
 ({filteredCopperknob.length})
 </span>
 )}
 </TabsTrigger>
 <TabsTrigger 
 value="linedancer"
 className="data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-400"
 data-testid="tab-linedancer"
 >
 <Vote className="w-4 h-4 mr-2" />
 LineDancer UK
 <span className="ml-2 text-xs text-slate-300">
 {levelFilter !=='all' ? `(${filteredLinedancer.length})` :'by votes'}
 </span>
 </TabsTrigger>
 <TabsTrigger 
 value="eld"
 className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
 data-testid="tab-eld"
 >
 <Trophy className="w-4 h-4 mr-2" />
 ELD Most Viewed
 {levelFilter !=='all' && (
 <span className="ml-2 text-xs text-slate-300">
 ({filteredEld.length})
 </span>
 )}
 </TabsTrigger>
 </TabsList>

 {/* Active filter indicator */}
 {levelFilter !=='all' && (
 <div className="mb-6 flex items-center justify-center gap-2 text-sm">
 <span className="text-slate-300">Showing:</span>
 <Badge className={`${LEVEL_FILTERS.find(f => f.value === levelFilter)?.color} text-white`}>
 {LEVEL_FILTERS.find(f => f.value === levelFilter)?.label}
 </Badge>
 <button 
 onClick={() => setLevelFilter('all')}
 className="text-slate-300 hover:text-white underline ml-2"
 >
 Clear filter
 </button>
 </div>
 )}

 <TabsContent value="copperknob">
 {filteredCopperknob.length === 0 ? (
 <div className="text-center py-20">
 <p className="text-slate-300 mb-4">
 {copperknobDances.length === 0 
 ?'Unable to load CopperKnob dances' 
 : `No ${levelFilter} level dances found`}
 </p>
 {copperknobDances.length === 0 ? (
 <Button onClick={fetchAllDances} variant="outline">
 <RefreshCw className="w-4 h-4 mr-2" />
 Try Again
 </Button>
 ) : (
 <Button onClick={() => setLevelFilter('all')} variant="outline">
 Show All Levels
 </Button>
 )}
 </div>
 ) : (
 <>
 <div className="grid gap-3">
 {filteredCopperknob.map((dance, index) => (
 <DanceCard key={dance.rank} dance={dance} source="ck" index={index} />
 ))}
 </div>
 
 {/* Attribution */}
 <div className="mt-10 text-center">
 <p className="text-slate-300 text-sm">
 Data sourced from{''}
 <a 
 href="https://www.copperknob.co.uk/mostpopular" 
 target="_blank" 
 rel="noopener noreferrer"
 className="text-amber-400 hover:text-amber-300"
 >
 CopperKnob.co.uk
 </a>
 {lastUpdatedCK && (
 <> • Last updated: {new Date(lastUpdatedCK).toLocaleDateString()}</>
 )}
 </p>
 </div>
 </>
 )}
 </TabsContent>

 <TabsContent value="linedancer">
 {filteredLinedancer.length === 0 ? (
 <div className="text-center py-20">
 <p className="text-slate-300 mb-4">
 {linedancerDances.length === 0 
 ?'Unable to load LineDancer dances' 
 : `No ${levelFilter} level dances found`}
 </p>
 {linedancerDances.length === 0 ? (
 <Button onClick={fetchAllDances} variant="outline">
 <RefreshCw className="w-4 h-4 mr-2" />
 Try Again
 </Button>
 ) : (
 <Button onClick={() => setLevelFilter('all')} variant="outline">
 Show All Levels
 </Button>
 )}
 </div>
 ) : (
 <>
 <div className="grid gap-3">
 {filteredLinedancer.map((dance, index) => (
 <DanceCard key={dance.rank} dance={dance} source="ld" index={index} />
 ))}
 </div>
 
 {/* Attribution */}
 <div className="mt-10 text-center">
 <p className="text-slate-300 text-sm">
 Data sourced from{''}
 <a 
 href="https://www.linedancerweb.com/index.php" 
 target="_blank" 
 rel="noopener noreferrer"
 className="text-violet-400 hover:text-violet-300"
 >
 LineDancerWeb.com
 </a>
 {lastUpdatedLD && (
 <> • Last updated: {new Date(lastUpdatedLD).toLocaleDateString()}</>
 )}
 </p>
 <p className="text-slate-500 text-xs mt-2">
 Charts based on votes from UK dancers within the past 31 days
 </p>
 </div>
 </>
 )}
 </TabsContent>

 <TabsContent value="eld">
 {filteredEld.length === 0 ? (
 <div className="text-center py-20">
 <p className="text-slate-300 mb-4">
 {eldDances.length === 0 
 ?'No ELD dances loaded yet. Use the bookmarklet in Admin to import.' 
 : `No ${levelFilter} level dances found`}
 </p>
 {eldDances.length === 0 ? (
 <a 
 href="https://www.everythinglinedance.com/scripts-stats.php" 
 target="_blank" 
 rel="noopener noreferrer"
 className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300"
 >
 <ExternalLink className="w-4 h-4" />
 Visit ELD Most Viewed
 </a>
 ) : (
 <Button onClick={() => setLevelFilter('all')} variant="outline">
 Show All Levels
 </Button>
 )}
 </div>
 ) : (
 <>
 <div className="grid gap-3">
 {filteredEld.map((dance, index) => (
 <DanceCard key={dance.rank} dance={dance} source="eld" index={index} />
 ))}
 </div>
 
 {/* Attribution */}
 <div className="mt-10 text-center">
 <p className="text-slate-300 text-sm">
 Data sourced from{' '}
 <a 
 href="https://www.everythinglinedance.com/scripts-stats.php" 
 target="_blank" 
 rel="noopener noreferrer"
 className="text-emerald-400 hover:text-emerald-300"
 >
 EverythingLineDance.com
 </a>
 {lastUpdatedELD && (
 <> • Last updated: {new Date(lastUpdatedELD).toLocaleDateString()}</>
 )}
 </p>
 <p className="text-slate-500 text-xs mt-2">
 Most viewed dance scripts on Everything Line Dance
 </p>
 </div>
 </>
 )}
 </TabsContent>
 </Tabs>
 )}
 </div>
 </div>
 );
}
